import { AppPreference, WallpaperThemeRes } from "@/api/model"
import { AuthHeader, User } from "@/models/biz_model"
import type { Resolution } from "@/sdk"

const userListKey = "userList"
const currentUserKey = "currentUser"
const authKeyPrefix = "auth_"
const autoLoginKeyPrefix = "autoLogin_"

const localDevIdListKey = "localDevIdList"
const settingEntryPositionKey = "settingEntryPosition"
const loginWallpaperKey = "loginWallpaper"
const desktopIdWallpaperKey = "desktopIdWallpaper"
const wallpaperListKey = "wallpaperList"
const scaleRatioConfigKey = "scaleRatioConfigs"
const wallpaperListCacheExpireMs = 30 * 60 * 1000

interface LoginWallpaperCacheValue {
  src: string
  thumbSrc: string
}

interface WallpaperListCacheValue {
  list: WallpaperThemeRes[]
  updatedAt: number
}

type CachedUsers = { [key in User["account"]]: User }

function _getUserMap(): CachedUsers {
  const usersJson = localStorage.getItem(userListKey)
  if (usersJson == null) {
    return {}
  }
  const users: CachedUsers = JSON.parse(usersJson)
  return users
}

// 获取缓存的用户列表
function getUsers(): User[] {
  const users = _getUserMap()
  return Object.values(users)
}

// 缓存用户
function saveUser(user: User) {
  const users = _getUserMap()
  users[user.account] = user
  localStorage.setItem(userListKey, JSON.stringify(users))
}

// 获取当前登录的用户
function getCurrentUser(): User | null {
  const account = localStorage.getItem(currentUserKey)
  if (account == null) {
    return null
  }
  const users = _getUserMap()
  const currentUser = users[account]
  if (!currentUser) {
    return null
  }

  return currentUser
}

function getUser(account: string): User | null {
  const users = _getUserMap()
  return users[account] || null
}

// 设置当前登录的用户
function setCurrentUser(user: User | null) {
  if (user === null) {
    localStorage.removeItem(currentUserKey)
  } else {
    localStorage.setItem(currentUserKey, user.account)
  }
}

// 删除用户
function deleteUser(user: User) {
  const users = _getUserMap()
  if (users[user.account]) {
    // remove user
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete users[user.account]
    localStorage.setItem(userListKey, JSON.stringify(users))
    AuthCache.deleteAuth(user.account)
    AutoLoginCache.deleteConfig(user.account)
  }
}

export const UserCache = {
  getCurrentUser,
  setCurrentUser,
  getUsers,
  saveUser,
  deleteUser,
  getUser,
}

function getAuth(account: string): AuthHeader | null {
  const value = localStorage.getItem(`${authKeyPrefix}${account}`)
  if (value === null) return null
  try {
    const auth = JSON.parse(value) as Partial<AuthHeader>
    return auth.token && auth.companyUniqID !== undefined
      ? { token: auth.token, companyUniqID: auth.companyUniqID }
      : null
  } catch {
    return null
  }
}

function saveAuth(account: string, auth: AuthHeader) {
  localStorage.setItem(`${authKeyPrefix}${account}`, JSON.stringify(auth))
}

function deleteAuth(account: string) {
  localStorage.removeItem(`${authKeyPrefix}${account}`)
}

export const AuthCache = { getAuth, saveAuth, deleteAuth }

export interface AutoLoginConfig {
  enabled: boolean
  deviceSecret?: string
  disabledByAdmin?: boolean
}

function getAutoLoginConfig(account: string): AutoLoginConfig {
  const value = localStorage.getItem(`${autoLoginKeyPrefix}${account}`)
  if (value === null) return { enabled: false }
  try {
    const config = JSON.parse(value) as Partial<AutoLoginConfig>
    return {
      enabled: config.enabled === true,
      deviceSecret: config.deviceSecret,
      disabledByAdmin: config.disabledByAdmin === true,
    }
  } catch {
    return { enabled: false }
  }
}

function saveAutoLoginConfig(account: string, config: AutoLoginConfig) {
  localStorage.setItem(
    `${autoLoginKeyPrefix}${account}`,
    JSON.stringify(config)
  )
}

function updateAutoLoginConfig(
  account: string,
  updates: Partial<AutoLoginConfig>
) {
  saveAutoLoginConfig(account, { ...getAutoLoginConfig(account), ...updates })
}

function deleteAutoLoginConfig(account: string) {
  localStorage.removeItem(`${autoLoginKeyPrefix}${account}`)
}

export const AutoLoginCache = {
  getConfig: getAutoLoginConfig,
  saveConfig: saveAutoLoginConfig,
  updateConfig: updateAutoLoginConfig,
  deleteConfig: deleteAutoLoginConfig,
}

// 保存是否允许音频输入状态
function saveAudioInputEnable(deviceId: number, enable: boolean) {
  localStorage.setItem(`audioInputEnable_${deviceId}`, enable.toString())
}

// 删除音频输入状态
function deleteAudioInputEnable(deviceId: number) {
  localStorage.removeItem(`audioInputEnable_${deviceId}`)
}

// 获取音频输入状态
function getAudioInputEnable(deviceId: number): boolean {
  const enable = localStorage.getItem(`audioInputEnable_${deviceId}`)
  return enable === "true"
}

// 保存是否允许视频输入状态
function saveVideoInputEnable(deviceId: number, enable: boolean) {
  localStorage.setItem(`videoInputEnable_${deviceId}`, enable.toString())
}

// 删除视频输入状态
function deleteVideoInputEnable(deviceId: number) {
  localStorage.removeItem(`videoInputEnable_${deviceId}`)
}

// 获取视频输入状态
function getVideoInputEnable(deviceId: number): boolean {
  const enable = localStorage.getItem(`videoInputEnable_${deviceId}`)
  return enable === "true"
}

// 更新app配置
function setAppPreference(preference: Partial<AppPreference>) {
  const appSetting = getAppPreference() ?? {}
  localStorage.setItem(
    "appPreference",
    JSON.stringify({ ...appSetting, ...preference })
  )
}

// 获取app配置
function getAppPreference(): AppPreference | null {
  const preferenceJson = localStorage.getItem("appPreference")
  if (preferenceJson == null) {
    return null
  }
  return JSON.parse(preferenceJson)
}

export const AudioInputCache = {
  saveAudioInputEnable,
  deleteAudioInputEnable,
  getAudioInputEnable,
}

export const VideoInputCache = {
  saveVideoInputEnable,
  deleteVideoInputEnable,
  getVideoInputEnable,
}

export const AppPreferenceCache = {
  setAppPreference,
  getAppPreference,
}

interface CachedScaleRatioConfig {
  user: string
  screenIndex: number
  width: number
  height: number
  recommended: number
  scaleRatios: number[]
  selected?: number
}

type ScaleRatioConfigMap = Record<string, CachedScaleRatioConfig>

function getScaleRatioConfigMap(): ScaleRatioConfigMap {
  const value = localStorage.getItem(scaleRatioConfigKey)
  if (value === null) return {}

  try {
    return JSON.parse(value) as ScaleRatioConfigMap
  } catch {
    return {}
  }
}

function getScaleRatioConfigMapKey(
  user: string,
  screenIndex: number,
  resolution: Resolution
): string {
  return JSON.stringify([
    user,
    screenIndex,
    resolution.width,
    resolution.height,
  ])
}

function getScaleRatioConfig(
  user: string,
  screenIndex: number,
  resolution: Resolution
): CachedScaleRatioConfig | null {
  const cacheMap = getScaleRatioConfigMap()
  return (
    cacheMap[getScaleRatioConfigMapKey(user, screenIndex, resolution)] ?? null
  )
}

function saveScaleRatioConfig({
  user,
  screenIndex,
  resolution,
  recommended,
  scaleRatios,
  selected,
}: {
  user: string
  screenIndex: number
  resolution: Resolution
  recommended?: number
  scaleRatios?: number[]
  selected?: number
}): void {
  const cacheMap = getScaleRatioConfigMap()
  const key = getScaleRatioConfigMapKey(user, screenIndex, resolution)
  const previous = cacheMap[key]
  if (
    previous === undefined &&
    (recommended === undefined || scaleRatios === undefined)
  ) {
    return
  }

  cacheMap[key] = {
    user,
    screenIndex,
    width: resolution.width,
    height: resolution.height,
    recommended: recommended ?? previous.recommended,
    scaleRatios: scaleRatios ?? previous.scaleRatios,
    selected: selected ?? previous?.selected,
  }
  localStorage.setItem(scaleRatioConfigKey, JSON.stringify(cacheMap))
}

export const ScaleRatioConfigCache = {
  getScaleRatioConfig,
  saveScaleRatioConfig,
}

function setSettingEntryPosition(pos: { x: number; y: number }) {
  localStorage.setItem(settingEntryPositionKey, JSON.stringify(pos))
}

function getSettingEntryPosition(): { x: number; y: number } | null {
  const posJson = localStorage.getItem(settingEntryPositionKey)
  if (posJson == null) {
    return null
  }
  return JSON.parse(posJson)
}

export const SettingEntryPosCache = {
  savePosition: setSettingEntryPosition,
  getPosition: getSettingEntryPosition,
}

function parseLoginWallpaperMetadata(
  value: string | null
): LoginWallpaperCacheValue | null {
  if (value == null) {
    return null
  }

  try {
    const parsed = JSON.parse(value) as Partial<LoginWallpaperCacheValue> & {
      file?: string
      preview?: string
    }
    return {
      src: parsed.src ?? parsed.file ?? "",
      thumbSrc:
        parsed.thumbSrc ?? parsed.preview ?? parsed.file ?? parsed.src ?? "",
    }
  } catch {
    return {
      src: value,
      thumbSrc: value,
    }
  }
}

function getLoginWallpaperMetadata(): LoginWallpaperCacheValue | null {
  return parseLoginWallpaperMetadata(localStorage.getItem(loginWallpaperKey))
}

function persistLoginWallpaperMetadata(
  metadata: LoginWallpaperCacheValue
): void {
  localStorage.setItem(loginWallpaperKey, JSON.stringify(metadata))
}

function setLoginWallpaper(value: LoginWallpaperCacheValue | string): void {
  if (typeof value === "string") {
    persistLoginWallpaperMetadata({
      src: value,
      thumbSrc: value,
    })
    return
  }

  persistLoginWallpaperMetadata(value)
}

function getLoginWallpaper(): string | null {
  const metadata = getLoginWallpaperMetadata()
  return metadata?.thumbSrc ?? null
}

function getLoginWallpaperSource(): string | null {
  const metadata = getLoginWallpaperMetadata()
  return metadata?.src ?? null
}

function getLoginWallpaperThumbSource(): string | null {
  const metadata = getLoginWallpaperMetadata()
  return metadata?.thumbSrc ?? null
}

export const LoginWallpapeCache = {
  setLoginWallpaper,
  getLoginWallpaper,
  getLoginWallpaperSource,
  getLoginWallpaperThumbSource,
}

type DesktopWallpaperMap = Record<string, string>

function getDesktopIdWallpaperMap(): DesktopWallpaperMap {
  const desktopIdWallpaperJson = localStorage.getItem(desktopIdWallpaperKey)
  if (desktopIdWallpaperJson == null) {
    return {}
  }
  return JSON.parse(desktopIdWallpaperJson) as DesktopWallpaperMap
}

function setDesktopIdWallpaper(desktopId: string, wallpaperUrl: string) {
  const currentMap = getDesktopIdWallpaperMap()
  currentMap[desktopId] = wallpaperUrl
  localStorage.setItem(desktopIdWallpaperKey, JSON.stringify(currentMap))
}

function getDesktopIdWallpaper(desktopId: string): string | null {
  const currentMap = getDesktopIdWallpaperMap()
  return currentMap[desktopId] ?? null
}

export const DesktopIdWallpaperCache = {
  setDesktopIdWallpaper,
  getDesktopIdWallpaper,
}

type WallpaperListCacheMap = Record<string, WallpaperListCacheValue>

function getWallpaperListCacheMap(): WallpaperListCacheMap {
  const wallpaperListJson = localStorage.getItem(wallpaperListKey)
  if (wallpaperListJson == null) {
    return {}
  }

  return JSON.parse(wallpaperListJson) as WallpaperListCacheMap
}

function getWallpaperListCacheOwner(): string | null {
  return getCurrentUser()?.account ?? null
}

function setWallpaperList(list: WallpaperThemeRes[]): void {
  const owner = getWallpaperListCacheOwner()
  if (owner == null) {
    return
  }

  const cacheMap = getWallpaperListCacheMap()
  cacheMap[owner] = {
    list,
    updatedAt: Date.now(),
  }
  localStorage.setItem(wallpaperListKey, JSON.stringify(cacheMap))
}

function getWallpaperListStale(): WallpaperListCacheValue | null {
  const owner = getWallpaperListCacheOwner()
  if (owner == null) {
    return null
  }

  const cacheMap = getWallpaperListCacheMap()
  return cacheMap[owner] ?? null
}

function getWallpaperList(): WallpaperListCacheValue | null {
  const cachedWallpaperList = getWallpaperListStale()
  if (cachedWallpaperList == null) {
    return null
  }

  if (Date.now() - cachedWallpaperList.updatedAt > wallpaperListCacheExpireMs) {
    return null
  }

  return cachedWallpaperList
}

export const WallpaperListCache = {
  setWallpaperList,
  getWallpaperList,
  getWallpaperListStale,
}

function getLocalDevIdPool(): Set<string> {
  const poolStr = localStorage.getItem(localDevIdListKey)
  if (!poolStr) {
    return new Set<string>()
  }
  const parsed = JSON.parse(poolStr)
  // 确保解析出来的是字符串数组
  if (Array.isArray(parsed)) {
    return new Set<string>(parsed.map(String))
  }
  return new Set<string>()
}

function setLocalDevIdPool(pool: Set<string>) {
  localStorage.setItem(localDevIdListKey, JSON.stringify(Array.from(pool)))
}

// 从资源池中随机取出一个 localDevId
function getRandomLocalDevIdFromPool(pool: Set<string>) {
  const keys = Array.from(pool.keys())
  const randomIndex = Math.floor(Math.random() * keys.length)
  const selectedId = keys[randomIndex]
  pool.delete(selectedId) // 从池中移除已选中的ID
  setLocalDevIdPool(pool) // 更新 localStorage
  return selectedId
}

// 当 localDevId 不再需要时，将其放回资源池
function returnLocalDevIdToPool(localDevId: string) {
  const pool = getLocalDevIdPool()
  pool.add(localDevId)
  setLocalDevIdPool(pool)
}

export const LocalDevIdCache = {
  getLocalDevIdPool,
  getRandomLocalDevIdFromPool,
  returnLocalDevIdToPool,
  setLocalDevIdPool,
}

// 修改初始密码提示：同一账号一天内只提示一次
const changePwdReminderKey = (account: string) => `changePwdReminder_${account}`

function isChangePwdReminderShownToday(account: string): boolean {
  const today = new Date().toDateString()
  return localStorage.getItem(changePwdReminderKey(account)) === today
}

function markChangePwdReminderShown(account: string): void {
  const today = new Date().toDateString()
  localStorage.setItem(changePwdReminderKey(account), today)
}

export const ChangePasswordReminderCache = {
  isShownToday: isChangePwdReminderShownToday,
  markShown: markChangePwdReminderShown,
}

// ===== 客户端动态配置缓存（对齐 android ClientConfigStore）=====
// localStorage 持久化 ETag、配置 body、已上传日志 requestId，用于 ETag 条件请求与日志去重。

const clientConfigEtagKey = "clientConfigEtag"
const clientConfigBodyKey = "clientConfigBody"
const clientConfigUploadedReqIdKey = "clientConfigUploadedReqId"

function getEtag(): string | null {
  return localStorage.getItem(clientConfigEtagKey)
}

function setEtag(etag: string): void {
  localStorage.setItem(clientConfigEtagKey, etag)
}

function getBody<T = unknown>(): T | null {
  const value = localStorage.getItem(clientConfigBodyKey)
  if (value == null) {
    return null
  }
  try {
    return JSON.parse(value) as T
  } catch {
    return null
  }
}

function setBody(body: unknown): void {
  localStorage.setItem(clientConfigBodyKey, JSON.stringify(body))
}

function getUploadedRequestId(): string | null {
  return localStorage.getItem(clientConfigUploadedReqIdKey)
}

function setUploadedRequestId(reqId: string): void {
  localStorage.setItem(clientConfigUploadedReqIdKey, reqId)
}

function clearClientConfig(): void {
  localStorage.removeItem(clientConfigEtagKey)
  localStorage.removeItem(clientConfigBodyKey)
  localStorage.removeItem(clientConfigUploadedReqIdKey)
}

export const ClientConfigCache = {
  getEtag,
  setEtag,
  getBody,
  setBody,
  getUploadedRequestId,
  setUploadedRequestId,
  clear: clearClientConfig,
}
